import React, { useState, useMemo } from 'react';

import {
  ThunderboltOutlined,
  SettingOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import {
  Modal,
  Checkbox,
  InputNumber,
  Typography,
  Tag,
  Divider,
  Space,
  Alert,
  Button,
  Tabs,
} from 'antd';

import { useDb } from '../db/useDataService';
import * as ds from '../db/dataService';
import SchoolImageLibrary from './SchoolImageLibrary';

import {
  LifeSphere,
  SemanticUnit,
  Angle,
  AngleType,
  ANGLE_TYPE_CONFIG,
  SmOffer,
  Ad,
  AdPlatform,
  AdStatus,
  YandexDirectGenerationSettings,
} from '../types';

import YandexDirectSettings, { getDefaultYandexDirectSettings } from './YandexDirectSettings';

const { Text } = Typography;
const { TabPane } = Tabs;

// --- Templates for generation ---

const DEFAULT_TEMPLATE = {
  titles: ['«{unit}» — решение есть', 'Узнайте про «{unit}»', 'Новый подход к «{unit}»'],
  title2s: ['Подробнее', 'Узнайте больше', 'Начните сейчас'],
  texts: ['{offer} — конкретное решение «{unit}».', 'Присоединяйтесь к {offer}. Подробности по ссылке.', '{offer} даст вам план действий по «{unit}».'],
};

const YA_TEMPLATES: Partial<Record<AngleType, { titles: string[]; title2s: string[]; texts: string[] }>> = {
  pain: {
    titles: [
      'А если через год ничего не изменится?',
      'Устали от «{unit}»?',
      'Каждый день без действий — потерянные деньги',
    ],
    title2s: ['Есть выход', 'Начните сейчас', 'Узнайте как'],
    texts: [
      '{offer}: разберём как решить «{unit}» за 3 шага.',
      'Перестаньте терпеть. {offer} — первый шаг к переменам.',
      'Сколько ещё ждать? {offer} покажет выход.',
    ],
  },
  dream: {
    titles: [
      'Представьте: «{unit}» — это реально',
      'Хотите изменить жизнь?',
      'Мечтаете о переменах?',
    ],
    title2s: ['Начните сегодня', 'Это возможно', 'Первый шаг'],
    texts: [
      '{offer} — ваш путь к мечте. Начните прямо сейчас.',
      'Тысячи людей уже добились перемен. {offer} — ваш шанс.',
      'Перемены начинаются с одного шага. {offer}.',
    ],
  },
  secret: {
    titles: [
      'Метод, который меняет всё',
      'Секрет, о котором молчат',
      'Что если всё проще, чем кажется?',
    ],
    title2s: ['Узнайте бесплатно', 'Подробнее', 'Смотрите'],
    texts: [
      'Один подход, который решает «{unit}». {offer} — узнайте.',
      'То, что вы узнаете на {offer}, изменит ваш взгляд.',
      'Неочевидное решение для «{unit}». Подробности на {offer}.',
    ],
  },
  social_proof: {
    titles: [
      '73% участников решили эту проблему',
      'Более 500 человек уже с нами',
      'Они тоже сомневались — а теперь довольны',
    ],
    title2s: ['Присоединяйтесь', 'Реальные кейсы', 'Результаты'],
    texts: [
      'Реальные истории тех, кто справился с «{unit}». {offer}.',
      '500+ участников уже прошли этот путь. {offer}.',
      'Кейсы и результаты на {offer}. Убедитесь сами.',
    ],
  },
  provocation: {
    titles: [
      'Ваша «стабильность» — это ловушка',
      'Хватит обманывать себя',
      'Ещё год будете терпеть?',
    ],
    title2s: ['Пора действовать', 'Хватит ждать', 'Решайтесь'],
    texts: [
      'Сколько можно мириться с «{unit}»? {offer} — время менять.',
      'Правда, которую не хочется слышать. {offer}.',
      'Перестаньте откладывать. {offer} — действуйте сейчас.',
    ],
  },
  instruction: {
    titles: [
      'Как решить «{unit}» за 5 шагов',
      'Пошаговый план: от проблемы к результату',
      '3 действия, которые изменят ситуацию',
    ],
    title2s: ['Пошаговый план', 'Подробный разбор', 'Инструкция'],
    texts: [
      'Конкретный план решения «{unit}». Разбираем на {offer}.',
      'Шаг за шагом: от «{unit}» к результату. {offer}.',
      'Понятная инструкция без воды. {offer} — начните сегодня.',
    ],
  },
  story: {
    titles: [
      'Как Мария справилась с «{unit}»',
      'История: из проблемы — в результат',
      'Реальный кейс решения «{unit}»',
    ],
    title2s: ['Читать историю', 'Реальный кейс', 'Подробности'],
    texts: [
      'Она тоже столкнулась с «{unit}» — и нашла выход. {offer}.',
      'Реальная история: как обычный человек решил проблему. {offer}.',
      'Этот кейс вдохновит вас действовать. {offer}.',
    ],
  },
  contrast: {
    titles: [
      'Было: «{unit}». Стало: всё иначе',
      'До и после: разница впечатляет',
      'Раньше мучился — теперь наслаждается',
    ],
    title2s: ['Смотрите разницу', 'До/после', 'Результат'],
    texts: [
      'Контраст, который говорит сам за себя. {offer}.',
      'Было: «{unit}». Стало: уверенность и результат. {offer}.',
      'Посмотрите, что изменилось после {offer}.',
    ],
  },
  expert: {
    titles: [
      'Эксперт с 10-летним опытом делится',
      'Метод, проверенный профессионалами',
      'Признанный подход к «{unit}»',
    ],
    title2s: ['От эксперта', 'Проверено', 'Авторитетно'],
    texts: [
      'Эксперт-практик раскрывает решение «{unit}». {offer}.',
      'Проверенная методика от профессионалов. {offer}.',
      'Доверьтесь опыту. {offer} — экспертный подход.',
    ],
  },
  urgency: {
    titles: [
      'Осталось 3 места — не упустите',
      'Только до пятницы',
      'Набор закрывается — успейте',
    ],
    title2s: ['Успейте записаться', 'Скоро закрытие', 'Последний шанс'],
    texts: [
      'Места заканчиваются. {offer} — запишитесь, пока не поздно.',
      'Скидка действует до конца недели. {offer} — не откладывайте.',
      'Те, кто записался вчера, уже начали. {offer} — ваша очередь.',
    ],
  },
};

const SITELINK_TEMPLATES = [
  'Программа', 'Стоимость', 'Отзывы', 'Кейсы выпускников',
  'Расписание', 'Преподаватели', 'FAQ', 'Гарантия',
];

const CALLOUT_TEMPLATES = [
  'Бесплатно', 'Диплом', 'Рассрочка 0%', 'Гарантия возврата',
  'Онлайн', 'С нуля', 'Практика с 1 дня', 'Наставник',
];

// --- Props ---

interface GenerateAdsModalProps {
  visible: boolean;
  onClose: () => void;
  onGenerate: (newAds: Ad[]) => void;
  spheres: LifeSphere[];
  angles: Angle[];
  offers: SmOffer[];
  productId: string;
}

// --- Helper: get all units from spheres ---

const getAllUnits = (spheres: LifeSphere[]): (SemanticUnit & { sphereName: string; themeName: string })[] => {
  const result: (SemanticUnit & { sphereName: string; themeName: string })[] = [];
  spheres.forEach((sphere) => {
    sphere.themes.forEach((category) => {
      category.subthemes.forEach((theme) => {
        theme.units.forEach((unit) => {
          result.push({ ...unit, sphereName: sphere.name, themeName: theme.name });
        });
      });
    });
  });
  return result;
};

// --- Component ---

const GenerateAdsModal: React.FC<GenerateAdsModalProps> = ({
  visible,
  onClose,
  onGenerate,
  spheres,
  angles,
  offers,
  productId,
}) => {
  const db = useDb();
  const allUnits = useMemo(() => getAllUnits(spheres), [spheres]);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const schoolId = useMemo(() => {
    const p = db.products.find((x) => x.id === productId);
    return p?.schoolId || `school-${productId}`;
  }, [db.products, productId]);
  const schoolImages = ds.listSchoolImages(schoolId);

  // Картинки: уже загруженные в существующих объявлениях + картинки тем
  // + общая библиотека школы (новый источник).
  const availableImages = useMemo(() => {
    const set = new Set<string>();
    db.campaignAds.forEach((a) => { if (a.image) set.add(a.image); });
    spheres.forEach((s) => s.themes.forEach((c) => {
      (c.images || []).forEach((u) => set.add(u));
    }));
    schoolImages.forEach((img) => set.add(img.url));
    return Array.from(set);
  }, [db.campaignAds, spheres, schoolImages]);

  const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);

  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setSelectedImage(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  // --- Step: 'select' or 'settings' ---
  const [step, setStep] = useState<'select' | 'settings'>('select');

  // --- Selection state ---
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<string>>(new Set());
  const [selectedAngleTypes, setSelectedAngleTypes] = useState<Set<AngleType>>(new Set());
  const [selectedOfferIds, setSelectedOfferIds] = useState<Set<string>>(new Set());
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<AdPlatform>>(new Set());
  const [variantsCount, setVariantsCount] = useState<number>(3);
  const [generating, setGenerating] = useState(false);

  // --- Platform settings ---
  const [yaSettings, setYaSettings] = useState<YandexDirectGenerationSettings>(
    getDefaultYandexDirectSettings()
  );

  // --- Available angle types ---
  const availableAngleTypes = useMemo(() => {
    if (selectedUnitIds.size === 0) return Object.keys(ANGLE_TYPE_CONFIG) as AngleType[];
    const types = new Set<AngleType>();
    angles.forEach((a) => {
      if (selectedUnitIds.has(a.unitId)) {
        types.add(a.type);
      }
    });
    return Array.from(types);
  }, [angles, selectedUnitIds]);

  // --- Total calculation ---
  const totalCombinations = useMemo(() => {
    const units = selectedUnitIds.size || 0;
    const angleTypes = selectedAngleTypes.size || 0;
    const ofrs = selectedOfferIds.size || 0;
    const platforms = selectedPlatforms.size || 0;
    return units * angleTypes * ofrs * platforms * variantsCount;
  }, [selectedUnitIds, selectedAngleTypes, selectedOfferIds, selectedPlatforms, variantsCount]);

  // --- Toggle helpers ---

  const toggleUnit = (id: string) => {
    setSelectedUnitIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllUnits = () => {
    if (selectedUnitIds.size === allUnits.length) {
      setSelectedUnitIds(new Set());
    } else {
      setSelectedUnitIds(new Set(allUnits.map((u) => u.id)));
    }
  };

  const toggleAngleType = (type: AngleType) => {
    setSelectedAngleTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const toggleAllAngleTypes = () => {
    if (selectedAngleTypes.size === availableAngleTypes.length) {
      setSelectedAngleTypes(new Set());
    } else {
      setSelectedAngleTypes(new Set(availableAngleTypes));
    }
  };

  const toggleOffer = (id: string) => {
    setSelectedOfferIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllOffers = () => {
    if (selectedOfferIds.size === offers.length) {
      setSelectedOfferIds(new Set());
    } else {
      setSelectedOfferIds(new Set(offers.map((o) => o.id)));
    }
  };

  const togglePlatform = (platform: AdPlatform) => {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);
      return next;
    });
  };

  const toggleAllPlatforms = () => {
    const all: AdPlatform[] = ['yandex_direct', 'vk'];
    if (selectedPlatforms.size === all.length) {
      setSelectedPlatforms(new Set());
    } else {
      setSelectedPlatforms(new Set(all));
    }
  };

  // --- Generate ---

  const truncate = (str: string, max: number): string => {
    return str.length > max ? str.slice(0, max - 1) + '…' : str;
  };

  const handleGenerate = () => {
    setGenerating(true);

    const newAds: Ad[] = [];
    let counter = Date.now();
    const limits = yaSettings.fieldLimits;

    selectedUnitIds.forEach((unitId) => {
      const unit = allUnits.find((u) => u.id === unitId);
      if (!unit) return;

      selectedAngleTypes.forEach((angleType) => {
        const angle = angles.find((a) => a.unitId === unitId && a.type === angleType);
        if (!angle) return;

        const templates = YA_TEMPLATES[angleType] || DEFAULT_TEMPLATE;

        selectedOfferIds.forEach((offerId) => {
          const offer = offers.find((o) => o.id === offerId);
          if (!offer) return;

          selectedPlatforms.forEach((platform) => {
            const isYa = platform === 'yandex_direct';

            for (let v = 0; v < variantsCount; v++) {
              const titleIdx = v % templates.titles.length;
              const title2Idx = v % templates.title2s.length;
              const textIdx = v % templates.texts.length;

              const fill = (tpl: string) =>
                tpl
                  .replace(/\{unit\}/g, unit.name.toLowerCase())
                  .replace(/\{offer\}/g, offer.name);

              const emptySl = { title: '', description: '', url: '' };
              const genSitelinksArr = isYa && yaSettings.generateSitelinks
                ? SITELINK_TEMPLATES
                    .slice(0, 4)
                    .map((sl) => ({
                      title: truncate(sl, limits.sitelinkTextMaxChars),
                      description: '',
                      url: 'example.com',
                    }))
                : [];
              while (genSitelinksArr.length < 4) genSitelinksArr.push({ ...emptySl });
              const genSitelinks = genSitelinksArr.slice(0, 4) as [typeof emptySl, typeof emptySl, typeof emptySl, typeof emptySl];

              const genCallouts = isYa && yaSettings.generateCallouts
                ? CALLOUT_TEMPLATES
                    .slice(0, limits.calloutsCount)
                    .map((c) => truncate(c, limits.calloutMaxChars))
                : [];

              const ad: Ad = {
                id: String(counter++),
                angleId: angle.id,
                offerId,
                platform,
                cabinetId: '',
                title: truncate(fill(templates.titles[titleIdx]), isYa ? limits.title1MaxChars : 100),
                title2: isYa && yaSettings.generateTitle2
                  ? truncate(fill(templates.title2s[title2Idx]), limits.title2MaxChars)
                  : '',
                text: truncate(fill(templates.texts[textIdx]), isYa ? limits.textMaxChars : 200),
                url: 'example.com',
                displayUrl: isYa && yaSettings.generateDisplayUrl
                  ? truncate('example.com', limits.displayUrlMaxChars)
                  : '',
                sitelinks: genSitelinks,
                callouts: genCallouts,
                actionButton: 'Перейти',
                status: 'draft' as AdStatus,
                image: selectedImage,
                imageText: '',
                imageButtonText: '',
              };
              newAds.push(ad);
            }
          });
        });
      });
    });

    setTimeout(() => {
      onGenerate(newAds);
      setGenerating(false);
      setStep('select');
      onClose();
      setSelectedUnitIds(new Set());
      setSelectedAngleTypes(new Set());
      setSelectedOfferIds(new Set());
      setSelectedPlatforms(new Set());
      setVariantsCount(3);
    }, 800);
  };

  // --- Render ---

  const renderSelectStep = () => (
    <>
      {/* СЕ */}
      <div className="gen-section">
        <div className="gen-section__header">
          <Text strong>Смысловые единицы</Text>
          <Checkbox
            checked={selectedUnitIds.size === allUnits.length && allUnits.length > 0}
            indeterminate={selectedUnitIds.size > 0 && selectedUnitIds.size < allUnits.length}
            onChange={toggleAllUnits}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>Все</Text>
          </Checkbox>
        </div>
        <div className="gen-section__items">
          {allUnits.map((unit) => (
            <Checkbox
              key={unit.id}
              checked={selectedUnitIds.has(unit.id)}
              onChange={() => toggleUnit(unit.id)}
            >
              <span style={{ fontSize: 13 }}>{unit.name}</span>
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                {unit.sphereName} → {unit.themeName}
              </Text>
            </Checkbox>
          ))}
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Углы */}
      <div className="gen-section">
        <div className="gen-section__header">
          <Text strong>Углы захода</Text>
          <Checkbox
            checked={selectedAngleTypes.size === availableAngleTypes.length && availableAngleTypes.length > 0}
            indeterminate={selectedAngleTypes.size > 0 && selectedAngleTypes.size < availableAngleTypes.length}
            onChange={toggleAllAngleTypes}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>Все</Text>
          </Checkbox>
        </div>
        <div className="gen-section__tags">
          {availableAngleTypes.map((type) => {
            const config = ANGLE_TYPE_CONFIG[type];
            const isSelected = selectedAngleTypes.has(type);
            return (
              <Tag
                key={type}
                color={isSelected ? 'blue' : undefined}
                style={{
                  cursor: 'pointer',
                  borderRadius: 4,
                  fontSize: 12,
                  opacity: isSelected ? 1 : 0.5,
                  border: isSelected ? undefined : '1px dashed #d9d9d9',
                }}
                onClick={() => toggleAngleType(type)}
              >
                {config.label}
              </Tag>
            );
          })}
          {availableAngleTypes.length === 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Сначала выберите СЕ с добавленными углами
            </Text>
          )}
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Офферы */}
      <div className="gen-section">
        <div className="gen-section__header">
          <Text strong>Офферы</Text>
          <Checkbox
            checked={selectedOfferIds.size === offers.length && offers.length > 0}
            indeterminate={selectedOfferIds.size > 0 && selectedOfferIds.size < offers.length}
            onChange={toggleAllOffers}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>Все</Text>
          </Checkbox>
        </div>
        <div className="gen-section__items">
          {offers.map((offer) => (
            <Checkbox
              key={offer.id}
              checked={selectedOfferIds.has(offer.id)}
              onChange={() => toggleOffer(offer.id)}
            >
              <span style={{ fontSize: 13 }}>{offer.name}</span>
            </Checkbox>
          ))}
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Площадки */}
      <div className="gen-section">
        <div className="gen-section__header">
          <Text strong>Площадки</Text>
          <Checkbox
            checked={selectedPlatforms.size === 2}
            indeterminate={selectedPlatforms.size === 1}
            onChange={toggleAllPlatforms}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>Все</Text>
          </Checkbox>
        </div>
        <div className="gen-section__items gen-section__items--row">
          <Checkbox
            checked={selectedPlatforms.has('yandex_direct')}
            onChange={() => togglePlatform('yandex_direct')}
          >
            Яндекс Директ
          </Checkbox>
          <Checkbox
            checked={selectedPlatforms.has('vk')}
            onChange={() => togglePlatform('vk')}
          >
            ВКонтакте
          </Checkbox>
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Количество вариантов */}
      <div className="gen-section">
        <div className="gen-section__header">
          <Text strong>Вариантов на комбинацию</Text>
        </div>
        <InputNumber
          min={1}
          max={10}
          value={variantsCount}
          onChange={(val) => setVariantsCount(val || 1)}
          style={{ width: 80 }}
        />
      </div>

      {/* Итого */}
      {totalCombinations > 0 && (
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          icon={<ThunderboltOutlined />}
          message={
            <span>
              Будет сгенерировано <Text strong>{totalCombinations}</Text> объявлений со статусом{' '}
              <Tag color="default" style={{ fontSize: 11 }}>Черновик</Tag>
            </span>
          }
          description={
            <Text type="secondary" style={{ fontSize: 12 }}>
              {selectedUnitIds.size} СЕ × {selectedAngleTypes.size} углов × {selectedOfferIds.size} офферов × {selectedPlatforms.size} площадок × {variantsCount} вариантов
            </Text>
          }
        />
      )}
    </>
  );

  const renderSettingsStep = () => (
    <div className="gen-settings">
      <div className="gen-settings__back">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => setStep('select')}
          style={{ padding: '4px 8px' }}
        >
          Назад к выбору
        </Button>
      </div>

      <Tabs defaultActiveKey="yandex_direct" size="small">
        {selectedPlatforms.has('yandex_direct') && (
          <TabPane tab="Яндекс Директ" key="yandex_direct">
            <YandexDirectSettings
              settings={yaSettings}
              onChange={setYaSettings}
            />
          </TabPane>
        )}
        {selectedPlatforms.has('vk') && (
          <TabPane tab="ВКонтакте" key="vk">
            <div style={{ padding: '24px 0', textAlign: 'center' }}>
              <Text type="secondary">Настройки для VK будут добавлены позже</Text>
            </div>
          </TabPane>
        )}
      </Tabs>

      <Divider />

      <div>
        <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>Картинка для объявлений</Text>
          <Space>
            {selectedImage && (
              <Button size="small" onClick={() => setSelectedImage(undefined)}>Сбросить</Button>
            )}
            <Button size="small" onClick={() => setLibraryOpen(true)}>Из библиотеки школы</Button>
            <label className="gen-image-upload-btn">
              <Button size="small" icon={<UploadOutlined />}>Загрузить новую</Button>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); }}
              />
            </label>
          </Space>
        </div>
        {availableImages.length === 0 && !selectedImage ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Нет загруженных картинок. Загрузите новую — она будет применена ко всем сгенерированным объявлениям.
          </Text>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
            {selectedImage && !availableImages.includes(selectedImage) && (
              <div
                key="uploaded"
                onClick={() => setSelectedImage(selectedImage)}
                style={{ position: 'relative', cursor: 'pointer', border: '2px solid #1890ff', borderRadius: 6, overflow: 'hidden', height: 80 }}
              >
                <img src={selectedImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <CheckCircleFilled style={{ position: 'absolute', top: 4, right: 4, color: '#1890ff', fontSize: 18, background: '#fff', borderRadius: '50%' }} />
              </div>
            )}
            {availableImages.map((url) => {
              const isSelected = selectedImage === url;
              return (
                <div
                  key={url}
                  onClick={() => setSelectedImage(url)}
                  style={{
                    position: 'relative',
                    cursor: 'pointer',
                    border: isSelected ? '2px solid #1890ff' : '1px solid #f0f0f0',
                    borderRadius: 6,
                    overflow: 'hidden',
                    height: 80,
                  }}
                >
                  <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  {isSelected && <CheckCircleFilled style={{ position: 'absolute', top: 4, right: 4, color: '#1890ff', fontSize: 18, background: '#fff', borderRadius: '50%' }} />}
                </div>
              );
            })}
          </div>
        )}
        {selectedImage && (
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
            Выбранная картинка будет применена ко всем сгенерированным объявлениям. Позже можно поменять в каждом отдельно.
          </Text>
        )}
      </div>
    </div>
  );

  const canGoToSettings = totalCombinations > 0;

  return (
    <Modal
      title={
        <Space>
          <ThunderboltOutlined style={{ color: '#faad14' }} />
          {step === 'select' ? 'Генерация объявлений' : 'Настройки генерации'}
        </Space>
      }
      visible={visible}
      onCancel={() => {
        onClose();
        setStep('select');
      }}
      width={step === 'settings' ? 700 : 640}
      destroyOnClose
      footer={
        step === 'select' ? (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              icon={<SettingOutlined />}
              onClick={() => setStep('settings')}
              disabled={!canGoToSettings}
            >
              Настройки площадок
            </Button>
            <Space>
              <Button onClick={onClose}>Отмена</Button>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={handleGenerate}
                disabled={totalCombinations === 0 || generating}
                loading={generating}
                style={totalCombinations > 0 ? { backgroundColor: '#faad14', borderColor: '#faad14' } : {}}
              >
                {generating ? 'Генерация...' : `Сгенерировать (${totalCombinations})`}
              </Button>
            </Space>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => setStep('select')}
            >
              Назад к выбору
            </Button>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={handleGenerate}
              disabled={totalCombinations === 0 || generating}
              loading={generating}
              style={totalCombinations > 0 ? { backgroundColor: '#faad14', borderColor: '#faad14' } : {}}
            >
              {generating ? 'Генерация...' : `Сгенерировать (${totalCombinations})`}
            </Button>
          </div>
        )
      }
    >
      {step === 'select' ? renderSelectStep() : renderSettingsStep()}
      <SchoolImageLibrary
        visible={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        schoolId={schoolId}
        selectedUrl={selectedImage}
        onSelect={(img) => setSelectedImage(img.url)}
      />
    </Modal>
  );
};

export default GenerateAdsModal;
